import { Observable } from "rxjs";
import { DynamicField } from "./dynamic-field.model";
import { DynamicRow } from "./dynamic-row.model";
import { VisibleOn } from "../visible-on.model";
import { AsyncValidatorFn, ValidatorFn } from "@angular/forms";

export class DynamicCol<T> {

    span?: string;
    formProperty?: string;
    isArray?: boolean;
    field?: DynamicField<T>;
    rows?: Array<DynamicRow<T>>;
    classes?: string;
    visible?: boolean;
    visibleObserver?: Observable<boolean>;
    visibleOn?: VisibleOn;
    switchCaseValue?: any;
    type: 'generic' | 'item' | 'object' | 'template';

    /**
     * This is a constructor function that initializes properties and subscribes to an observable to
     * update the visibility of the object.
     * @param source - A partial object of type DynamicCol<T>.
     */
    constructor(source: Partial<DynamicCol<T>>) {
        this.visible = true;
        this.isArray = true;
        this.type = "generic";
        Object.assign(this, source);
        if (!this.classes) {
            this.classes = 'mt-4';
        }

        this.visibleObserver?.subscribe(value => {
            this.visible = value;
            if (this.visibleOn) {
                if (this.visibleOn.clearFieldValidations) {
                    value ? this.setFieldValidations() : this.clearFieldValidations();
                }
            }
        });
    }

    /**
     * The function sets an observer for a boolean value and updates the visibility of a field based on
     * the value received from the observer.
     * @param observer - An Observable<boolean> object that emits boolean values.
     */
    setVisibleObserver(observer: Observable<boolean>) {

        this.visibleObserver = observer;

        this.visibleObserver.subscribe(value => {
            this.visible = value;
            if (this.visibleOn?.clearFieldValidations) {
                value ? this.setFieldValidations() : this.clearFieldValidations();
            }
        });
    }

    /**
     * The function clears the async and regular validators of a form control and updates its value and
     * validity.
     */
    clearFieldValidations() {

        this.field?.formControl?.setAsyncValidators(null);
        this.field?.formControl?.setValidators(null);
        this.field?.formControl?.updateValueAndValidity();
    }

    /**
     * The function sets the validators and async validators for a form control based on the validators
     * defined in the field object.
     */
    setFieldValidations() {

        let validators: (ValidatorFn)[] | undefined = this.field?.validators?.map(item => item.validator);
        let asyncValidators: (AsyncValidatorFn)[] | undefined = this.field?.validators?.map(item => item.asyncValidator);

        if (validators) {
            this.field?.formControl?.setValidators(validators);
        }

        if (asyncValidators && !asyncValidators.some(item => item === undefined)) {
            this.field?.formControl?.setAsyncValidators(asyncValidators);
        }
    }
}